import {
  Button,
  Callout,
  Classes,
  Divider,
  FormGroup,
  H4,
  H5,
  H6,
  InputGroup,
  Intent,
  PopoverPosition,
  Pre,
  Spinner,
  SpinnerSize,
  Tab,
  Tabs,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import {
  Cell,
  Column,
  ColumnHeaderCell2,
  EditableName,
  Table2,
} from '@blueprintjs/table';
import classNames from 'classnames';
import * as React from 'react';
import { Text, Tooltip } from 'vet-bones/components';

import { AdvancedOptions } from 'src/components/AdvancedOptions';
import { DataSourceTableSelectItem } from 'src/constants/dataSourceTable';
import {
  TEST_IDS,
  getProjectVirtualGraphPanelColumnLabelTestId,
  getProjectVirtualGraphPanelPreviewTabTestId,
} from 'src/constants/testIds';
import { QueryStatus } from 'src/constants/virtualGraphs';
import {
  ProjectVirtualGraphToCreate,
  ProjectVirtualGraphsToCreate,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/types';
import { getTablesFromVgsToCreate } from 'src/containers/ProjectVirtualGraphPanel/utils/getTablesFromVgsToCreate';
import { sortVirtualGraphs } from 'src/containers/ProjectVirtualGraphPanel/utils/sortVirtualGraphs';
import {
  ProjectVirtualGraph,
  ProjectVirtualGraphField,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';

type ProjectVirtualGraphPreviewProps = {
  disableEditing?: boolean;
  enablePreviewRefresh?: boolean;
  existingVirtualGraph?: ProjectVirtualGraph;
  handleUpdateVirtualGraph?: (
    id: string,
    update: Partial<ProjectVirtualGraphToCreate>
  ) => void;
  isUsingCustomQuery: boolean;
  refreshPreview: () => void;
  runningQueryStatus: QueryStatus;
  selectedTab: string;
  setSelectedTab: (tabId: string) => void;
  virtualGraphsToCreate: ProjectVirtualGraphsToCreate;
};

type ProjectVirtualGraphPreviewTableProps = {
  disableEditing?: boolean;
  emptyMessage?: string;
  fields: ProjectVirtualGraphField[];
  handleFieldUpdate?: (label: string, colIdx: number) => void;
  rows: string[][];
};

const ProjectVirtualGraphPreviewTable: React.VFC<
  ProjectVirtualGraphPreviewTableProps
> = ({ disableEditing, fields, handleFieldUpdate, rows, emptyMessage }) => {
  return (
    <>
      <div
        className="sd-project-virtual-graph-panel__table"
        data-heap-redact-attributes="title"
        data-testid={TEST_IDS.projectVirtualGraphPanel.preview.table}
      >
        <Table2 enableRowHeader={false} numRows={rows.length}>
          {fields.map((field) => (
            <Column
              key={field.value}
              cellRenderer={(rowIdx, colIdx) => (
                <Cell>{rows[rowIdx][colIdx]}</Cell>
              )}
              columnHeaderCellRenderer={(colIdx) => (
                <ColumnHeaderCell2
                  className="sd-project-virtual-graph-panel-table__header"
                  name={fields[colIdx].label}
                  nameRenderer={(name) => (
                    <div
                      data-testid={getProjectVirtualGraphPanelColumnLabelTestId(
                        field.value
                      )}
                    >
                      {disableEditing ? (
                        <Text className={Classes.TEXT_OVERFLOW_ELLIPSIS}>
                          {name}
                        </Text>
                      ) : (
                        <EditableName
                          name={name}
                          onChange={(label) =>
                            handleFieldUpdate?.(label, colIdx)
                          }
                        />
                      )}
                    </div>
                  )}
                />
              )}
              name={field.label}
            />
          ))}
        </Table2>
      </div>
      {rows.length === 0 && emptyMessage ? (
        <div className="sd-project-virtual-graph-panel__preview__centered">
          <Callout
            data-testid={TEST_IDS.projectVirtualGraphPanel.preview.emptyResults}
            intent={Intent.WARNING}
            title={emptyMessage}
          />
        </div>
      ) : null}
    </>
  );
};

export const ProjectVirtualGraphPreview: React.VFC<
  ProjectVirtualGraphPreviewProps
> = ({
  disableEditing,
  enablePreviewRefresh,
  existingVirtualGraph,
  handleUpdateVirtualGraph,
  isUsingCustomQuery,
  refreshPreview,
  runningQueryStatus,
  selectedTab,
  virtualGraphsToCreate,
  setSelectedTab,
}) => {
  let rowCount: number | null = null;
  let refreshButton: JSX.Element | null = null;
  let previewBody: JSX.Element | null = null;

  const virtualGraph = selectedTab
    ? virtualGraphsToCreate[selectedTab]
    : undefined;

  switch (runningQueryStatus) {
    case QueryStatus.QUERYING:
      refreshButton = (
        <Button
          data-testid={TEST_IDS.projectVirtualGraphPanel.preview.refreshButton}
          loading
          minimal
        />
      );
      previewBody = (
        <div className="sd-project-virtual-graph-panel__preview__centered">
          <Spinner size={SpinnerSize.LARGE} />
        </div>
      );
      break;
    case QueryStatus.NO_QUERY:
      if (isUsingCustomQuery) {
        previewBody = (
          <div className="sd-project-virtual-graph-panel__preview__centered">
            <H4>
              {
                copy.components.projectResourceDialog.projectVirtualGraphPanel
                  .customQuery
              }
            </H4>
            <Text>
              {
                copy.components.projectResourceDialog.projectVirtualGraphPanel
                  .preview.validateToPreview
              }
            </Text>
          </div>
        );
        break;
      }
      previewBody = (
        <div className="sd-project-virtual-graph-panel__preview__centered">
          <H4>
            {copy.components.projectResourceDialog.projectVirtualGraphPanel.selectTable(
              !existingVirtualGraph
            )}
          </H4>
          <Text>
            {
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .preview.selectToPreview
            }
          </Text>
        </div>
      );
      break;
    case QueryStatus.SUCCESS:
      rowCount = virtualGraph?.previewRows?.length || 0;
      previewBody = (
        <>
          <FormGroup
            inline
            label={
              <H6>
                {
                  copy.components.projectResourceDialog.projectVirtualGraphPanel
                    .form.name
                }
              </H6>
            }
            labelFor="sd-virtual-graph-name"
          >
            <InputGroup
              data-testid={TEST_IDS.projectVirtualGraphPanel.resourceName}
              disabled={disableEditing}
              fill
              id="sd-virtual-graph-name"
              name="name"
              onChange={(evt) => {
                if (!selectedTab) {
                  return;
                }

                handleUpdateVirtualGraph?.(selectedTab, {
                  name: evt.target.value,
                });
              }}
              value={virtualGraph?.name || ''}
            />
          </FormGroup>
          <ProjectVirtualGraphPreviewTable
            disableEditing={disableEditing}
            fields={virtualGraph?.fields || []}
            handleFieldUpdate={(label, colIdx) => {
              if (!selectedTab) {
                return;
              }

              handleUpdateVirtualGraph?.(selectedTab, {
                fields: virtualGraph?.fields.map((field) => ({
                  ...field,
                  label:
                    field.value === virtualGraph.fields[colIdx].value
                      ? label
                      : field.label,
                })),
              });
            }}
            rows={virtualGraph?.previewRows || []}
          />
        </>
      );
      break;
    case QueryStatus.EXISTING_ROWS:
      rowCount = existingVirtualGraph?.previewRows.length || 0;
      refreshButton = (
        <>
          <Tooltip
            content={
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .preview.clickToRefresh
            }
            lazy
            position={PopoverPosition.TOP}
          >
            <Button
              data-testid={
                TEST_IDS.projectVirtualGraphPanel.preview.refreshButton
              }
              minimal
              onClick={refreshPreview}
              rightIcon={IconNames.REFRESH}
            >
              {copy.components.projectResourceDialog.projectVirtualGraphPanel.preview.lastUpdated(
                existingVirtualGraph?.dateLastUpdated || Date.now()
              )}
            </Button>
          </Tooltip>
        </>
      );
      previewBody = (
        <>
          <FormGroup
            inline
            label={
              <H6>
                {
                  copy.components.projectResourceDialog.projectVirtualGraphPanel
                    .form.name
                }
              </H6>
            }
            labelFor="sd-virtual-graph-name"
          >
            <InputGroup
              data-testid={TEST_IDS.projectVirtualGraphPanel.resourceName}
              disabled={disableEditing}
              fill
              id="sd-virtual-graph-name"
              name="name"
              onChange={(evt) => {
                if (!selectedTab) {
                  return;
                }

                handleUpdateVirtualGraph?.(selectedTab, {
                  name: evt.target.value,
                });
              }}
              value={virtualGraph?.name || ''}
            />
          </FormGroup>
          <ProjectVirtualGraphPreviewTable
            disableEditing={disableEditing}
            emptyMessage={
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .preview.errors.previewEmpty
            }
            fields={existingVirtualGraph?.fields || []}
            handleFieldUpdate={(label, colIdx) => {
              if (!selectedTab) {
                return;
              }

              handleUpdateVirtualGraph?.(selectedTab, {
                fields: virtualGraph?.fields.map((field, fieldIdx) => ({
                  ...field,
                  label: fieldIdx === colIdx ? label : field.label,
                })),
              });
            }}
            rows={existingVirtualGraph?.previewRows || []}
          />
        </>
      );
      break;
    case QueryStatus.ERROR:
      previewBody = (
        <div className="sd-project-virtual-graph-panel__preview__centered">
          <Callout
            data-testid={TEST_IDS.projectVirtualGraphPanel.preview.error}
            intent={Intent.DANGER}
            title={
              virtualGraph?.errors?.length === 1
                ? copy.components.projectResourceDialog.projectVirtualGraphPanel
                    .preview.errors.badQueryOneError
                : copy.components.projectResourceDialog.projectVirtualGraphPanel
                    .preview.errors.badQueryMultipleErrors
            }
          >
            <AdvancedOptions
              text={() =>
                copy.components.projectResourceDialog.projectVirtualGraphPanel
                  .preview.errors.badQueryDetails
              }
            >
              {virtualGraph?.errors?.map(({ message }) => {
                return <Pre key={message}>{message}</Pre>;
              })}
            </AdvancedOptions>
          </Callout>
        </div>
      );
      break;
    default:
      previewBody = null;
  }

  if (!enablePreviewRefresh) {
    refreshButton = null;
  }

  return (
    <div
      className={classNames(
        'sd-project-virtual-graph-panel__preview',
        Classes.CALLOUT
      )}
    >
      <div className="sd-project-virtual-graph-panel__preview__header">
        <div>
          <H5>
            {
              copy.components.projectResourceDialog.projectVirtualGraphPanel
                .form.preview
            }
          </H5>
          <div
            className="sd-project-virtual-graph-panel__preview__header__row-count"
            data-testid={TEST_IDS.projectVirtualGraphPanel.preview.rowCount}
          >
            {rowCount !== null &&
              copy.components.projectResourceDialog.projectVirtualGraphPanel.form.previewRowCount(
                rowCount,
                Boolean(virtualGraph?.allResultsIncluded)
              )}
          </div>
        </div>
        {refreshButton}
      </div>
      {isUsingCustomQuery ||
      getTablesFromVgsToCreate(virtualGraphsToCreate).filter(
        (virtualGraph) => typeof virtualGraph.from === 'object'
      ).length < 2 ? (
        <>{previewBody}</>
      ) : (
        <Tabs
          animate={false}
          onChange={(tabId: string) => {
            setSelectedTab(tabId);
          }}
          renderActiveTabPanelOnly
          selectedTabId={selectedTab || ''}
        >
          {sortVirtualGraphs(virtualGraphsToCreate).map(
            (virtualGraph: ProjectVirtualGraphToCreate) => (
              <Tab
                key={virtualGraph.id}
                data-testid={getProjectVirtualGraphPanelPreviewTabTestId(
                  (virtualGraph.from as DataSourceTableSelectItem).text
                )}
                id={virtualGraph.id}
                panel={
                  <>
                    <Divider className="sd-project-virtual-graph-panel__preview__tab-divider" />
                    {previewBody}
                  </>
                }
                title={(virtualGraph.from as DataSourceTableSelectItem).text}
              />
            )
          )}
        </Tabs>
      )}
    </div>
  );
};
